import { useState, type ReactNode } from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  LayoutDashboard, Building2, Package, FileText, FileSignature, ShoppingCart,
  Users, Receipt, Landmark, Calculator, Terminal, Settings, LogOut, Menu,
} from "lucide-react";
import { useAuth } from "@/app/auth";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";

const NAV = [
  { to: "/", label: "Tableau de bord", icon: LayoutDashboard, end: true },
  { to: "/clients", label: "Clients", icon: Building2 },
  { to: "/catalogue", label: "Catalogue", icon: Package },
  { to: "/devis", label: "Devis", icon: FileText },
  { to: "/contrats", label: "Contrats", icon: FileSignature },
  { to: "/commandes", label: "Commandes", icon: ShoppingCart },
  { to: "/leads", label: "Leads", icon: Users },
  { to: "/factures", label: "Factures", icon: Receipt },
  { to: "/banque", label: "Banque", icon: Landmark },
  { to: "/comptabilite", label: "Comptabilité", icon: Calculator },
  { to: "/console", label: "Console", icon: Terminal },
  { to: "/parametres", label: "Paramètres", icon: Settings },
];

export function Brand() {
  return (
    <div className="flex items-center gap-2.5">
      <span className="grid h-8 w-8 place-items-center rounded-xl text-sm font-black text-white" style={{ background: "var(--gradient-primary)" }}>IC</span>
      <span className="flex items-baseline gap-1.5">
        <span className="font-heading text-base font-extrabold tracking-tight">IRONCLIC</span>
        <span className="text-[10px] font-semibold uppercase tracking-wider text-primary">CRM</span>
      </span>
    </div>
  );
}

function initials(email: string | undefined, name?: string | null) {
  const src = (name && name.trim()) || email || "?";
  return src.split(/[\s.@_-]+/).filter(Boolean).slice(0, 2).map((p) => p[0]!.toUpperCase()).join("");
}

function SidebarBody({ onNavigate }: { onNavigate?: () => void }) {
  const { user, roles, rolesLoading, signOut } = useAuth();
  const fullName = (user?.user_metadata as { full_name?: string } | undefined)?.full_name;
  return (
    <>
      <nav aria-label="Navigation principale" className="flex-1 space-y-1 px-3 py-2">
        {NAV.map(({ to, label, icon: Icon, end }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            onClick={onNavigate}
            className={({ isActive }) =>
              cn(
                "flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-primary/10 hover:text-foreground",
                isActive && "bg-primary text-primary-foreground shadow-md shadow-primary/25 hover:bg-primary hover:text-primary-foreground",
              )
            }
          >
            <Icon className="h-[18px] w-[18px]" />
            {label}
          </NavLink>
        ))}
      </nav>
      <div className="m-3 rounded-2xl bg-muted/70 p-3 text-xs text-muted-foreground">
        <div className="flex items-center gap-3">
          <div className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-primary/15 text-xs font-bold text-primary">{initials(user?.email, fullName)}</div>
          <div className="min-w-0">
            <div className="truncate font-semibold text-foreground" title={user?.email ?? ""}>{fullName || user?.email}</div>
            <div className="truncate" title={user?.email ?? ""}>{fullName ? user?.email : ""}</div>
            <div className="truncate">{rolesLoading ? "…" : roles.length ? roles.join(", ") : "aucun rôle"}</div>
          </div>
        </div>
        <Button variant="ghost" size="sm" className="mt-2 w-full justify-start rounded-xl" onClick={() => void signOut()}>
          <LogOut className="mr-2 h-4 w-4" /> Déconnexion
        </Button>
        <div className="mt-1 px-2 font-mono text-[10px] text-muted-foreground/70" data-testid="app-version">version {__APP_VERSION__}</div>
      </div>
    </>
  );
}

export function AppShell({ children }: { children: ReactNode }) {
  const location = useLocation();
  // Le tiroir de navigation (mobile) est ouvert « pour la page courante » : dès
  // que l'adresse change, il se referme tout seul, sans effet ni synchronisation.
  const [openFor, setOpenFor] = useState<string | null>(null);
  const open = openFor === location.pathname;
  const setOpen = (o: boolean) => setOpenFor(o ? location.pathname : null);

  return (
    <div className="min-h-screen p-2 sm:p-4 lg:p-5">
      <div className="glass-strong mx-auto flex min-h-[calc(100vh-1rem)] max-w-[1600px] overflow-hidden rounded-3xl sm:min-h-[calc(100vh-2rem)] lg:min-h-[calc(100vh-2.5rem)]">
        {/* Barre latérale fixe à partir de lg (≥ 1024 px) */}
        <aside className="hidden w-64 shrink-0 flex-col border-r border-border/70 bg-white/40 lg:flex">
          <div className="flex h-16 items-center px-5"><Brand /></div>
          <SidebarBody />
        </aside>

        <div className="flex min-w-0 flex-1 flex-col">
          {/* Barre supérieure avec menu, en dessous de lg */}
          <header className="flex h-14 items-center gap-2 border-b border-border/70 px-3 lg:hidden">
            <Button variant="ghost" size="icon" className="rounded-xl" aria-label="Ouvrir le menu" onClick={() => setOpen(true)}>
              <Menu className="h-5 w-5" />
            </Button>
            <Brand />
          </header>
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetContent side="left" className="flex w-72 flex-col p-0">
              <SheetTitle className="sr-only">Navigation</SheetTitle>
              <div className="flex h-16 items-center px-5"><Brand /></div>
              <SidebarBody onNavigate={() => setOpen(false)} />
            </SheetContent>
          </Sheet>

          <main className="min-w-0 flex-1 p-4 sm:p-6 lg:p-8">{children}</main>
        </div>
      </div>
    </div>
  );
}
